
/**
 * Draw block of text to the 2D canvas
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text to draw
 * @param {number} x - x position of the text anchor
 * @param {number} y - y position of the text anchor
 * @param {number} maxWidth - maximum width of the text
 * @param {number} lineHeight - distance between lines
 * @param {string} halign - horizontal alignment ("left", "center", "right")
 * @param {string} valign - vertical alignment of the text baseline ("top", "center", "bottom")
 */
export function drawTextBlock(ctx, text, x, y, maxWidth, lineHeight, halign = "left", valign = "top") {
	if (text.length === 0) return;
	const lines = breakLines(ctx, text, maxWidth);
	if (lines.length === 0) return;
	ctx.save();
	ctx.textAlign = halign;
	let baseline = y;
	if (valign === "middle") {
		baseline = Math.floor(baseline - lineHeight * (lines.length - 1) / 2);
	} else if (valign === "bottom") {
		baseline = Math.floor(baseline - lineHeight * (lines.length - 1));
	}
	for (let ln of lines) {
		ctx.fillText(ln, x, baseline, maxWidth);
		baseline += lineHeight;
	}
	ctx.restore();
}

/**
 * Draw block of text to the 2D canvas
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text to draw
 * @param {number} maxWidth - maximum width of the text
 */
function breakLines(ctx, text, maxWidth) {
	const hardLines = text.match(/[^\r\n]+/g);
	const lines = [];
	for (let hardLine of hardLines) {
		const wrapped = wrapLine(ctx, hardLine, maxWidth);
		for (let wl of wrapped) {
			lines.push(wl);
		}
	}
	return lines;
}

function wrapLine(ctx, text, maxWidth) {
	const lines = [];
	const words = text.split(/\s+/);

	let line = '';
	for (let i = 0; i < words.length; i++) {
		const testLine = line.length === 0 ? words[i] : line + ' ' + words[i];
		const metrics = ctx.measureText(testLine);
		const testWidth = metrics.width;
		if (testWidth > maxWidth) {
			if (line.length === 0) {
				// even one word is too much (accept overflow)
				lines.push(words[i]);
			} else {
				lines.push(line);
				line = '';
				i--;	// this word again
			}
		} else {
			line = testLine;
		}
	}
	if (line.length > 0) lines.push(line);
	return lines;
}


const TWO_PI = 2*Math.PI;
export function normalizeAngle(angle) {
	return angle - TWO_PI * Math.floor((angle + Math.PI) / TWO_PI);
}

/**
 * @param {Point} pointA first point of the line
 * @param {Point} pointB second point of the line
 * @param {Point} center center of the circle
 * @param {number} radius	radius of the circle
 * @return {Point[]} 0-2 points on the circle
 */
export function calcCircleLineIntersectionPoint(pointA, pointB, center, radius) {
	let baX = pointB.x - pointA.x;
	let baY = pointB.y - pointA.y;
	let caX = center.x - pointA.x;
	let caY = center.y - pointA.y;

	let a = baX * baX + baY * baY;
	let bBy2 = baX * caX + baY * caY;
	let c = caX * caX + caY * caY - radius * radius;

	let pBy2 = bBy2 / a;
	let q = c / a;

	let disc = pBy2 * pBy2 - q;
	if (disc < 0) {
		return [];
	}
	// if disc == 0 ... dealt with later
	let tmpSqrt = Math.sqrt(disc);
	let abScalingFactor1 = -pBy2 + tmpSqrt;
	let abScalingFactor2 = -pBy2 - tmpSqrt;

	let p1 = {
		x : pointA.x - baX * abScalingFactor1,
		y : pointA.y - baY * abScalingFactor1
	};
	if (disc === 0) { // abScalingFactor1 == abScalingFactor2
		return [ p1 ];
	}
	let p2 = {
		x : pointA.x - baX * abScalingFactor2,
		y : pointA.y - baY * abScalingFactor2
	};
	return [p1, p2];
}


/**
 * @param {Point} A
 * @param {Point} B
 * @param {Point} P
 * @return {Point}
 */
export function calcClosestPoint(A, B, P) {
	let a_to_p = [P.x - A.x, P.y - A.y];     // Storing vector A->P
	let a_to_b = [B.x - A.x, B.y - A.y];     // Storing vector A->B

	let atb2 = Math.pow(a_to_b[0],2) + Math.pow(a_to_b[1],2);
	// Basically finding the squared magnitude
	// of a_to_b

	let atp_dot_atb = a_to_p[0]*a_to_b[0] + a_to_p[1]*a_to_b[1];
	// The dot product of a_to_p and a_to_b

	let t = atp_dot_atb / atb2;              // The normalized "distance" from a to
	//   your closest point

	return {
		x: A.x + a_to_b[0]*t,
		y: A.y + a_to_b[1]*t
	};
	// Add the distance to A, moving
	//   towards B

}

/** This method works even if text starts or ends with whitespace */
export function measure(ctx: CanvasRenderingContext2D, text:string):TextMetrics {
	return ctx.measureText("\u200B" + text + "\u200B");
}


export interface TextRun {
	text: string;
	font: string;
	color: string;
	opacity?: number;
	noBreak?: boolean;
}

export interface MeasuredRun extends TextRun {
	x: number;
	ascent: number;
	descent: number;
}

export interface MeasuredLine {
	text: string;
	width: number;
	y: number;
	ascent: number;
	descent: number;
	runs: MeasuredRun[];
}

function addToCurentLine(runs: MeasuredRun[], text: string, x: number, ascent: number, descent: number, source: TextRun) {
	if (runs.length === 0) {
		runs.push({
			text: text,
			font: source.font,
			color: source.color,
			ascent,
			descent,
			opacity: source.opacity,
			x: x
		});
	} else {
		const last = runs[runs.length - 1];
		if (last.font === source.font && last.color === source.color && last.opacity === source.opacity) {
			last.text += text;
		} else {
			runs.push({
				text: text,
				font: source.font,
				color: source.color,
				ascent: ascent,
				descent: descent,
				opacity: source.opacity,
				x: x
			});
		}
	}
}

function maxAscent(runs:MeasuredRun[]) {
	let max = 0;
	runs.forEach((r) => {
		max = Math.max(max, r.ascent);
	});
	return max;
}

function maxDescent(runs:MeasuredRun[]) {
	let max = 0;
	runs.forEach((r) => {
		max = Math.max(max, r.descent);
	});
	return max;
}

export function measureTextLine(ctx:CanvasRenderingContext2D, runs: TextRun[], width:number):MeasuredLine {
	const line:MeasuredLine = {
		ascent: 0,
		descent: 0,
		runs: [],
		text: "",
		width: 0,
		y: 0
	};
	let lineStart:boolean = true;

	for (let r = 0; r < runs.length; r++) {
		const run = runs[r];

		ctx.font = run.font;
		const whiteSpaceStart = /^\s/.test(run.text.charAt(0));
		const whiteSpaceEnd = run.text.length > 1 && /^\s/.test(run.text.charAt(run.text.length - 1));
		const txt = run.text.trim();
		const words = txt.split(/\s+/);

		for (let w = 0; w < words.length; w++) {
			let word = words[w];
			const hasEndSpace = whiteSpaceEnd && w === (words.length - 1);	// relevant for last word
			const startingSpace = !lineStart && (w == 0 && whiteSpaceStart || w > 0);
			let wordWithSpace = (startingSpace ? " "+word : word);
			let m = measure(ctx, wordWithSpace);
			if ((line.width + m.width) > width) {
				// This word will not fit
				r = runs.length;	// no more runs will be processed this is the last word of last run
				w = words.length;
				while (wordWithSpace.length > 1) {
					wordWithSpace = wordWithSpace.substring(0, wordWithSpace.length - 2) + "…";
					m = measure(ctx, wordWithSpace);
					if ((line.width + m.width) <= width) {
						// It will fit compressed
						addToCurentLine(line.runs, wordWithSpace, line.width, m.fontBoundingBoxAscent, m.fontBoundingBoxDescent, run);
						line.text += wordWithSpace;
						line.width += m.width;
						return line;
					}
				}
			} else {
				// Great it will fit in
				if (hasEndSpace) {
					wordWithSpace += ' ';
					m = measure(ctx, wordWithSpace);
				}
				addToCurentLine(line.runs, wordWithSpace, line.width, m.fontBoundingBoxAscent, m.fontBoundingBoxDescent, run);
				line.text += wordWithSpace;
				line.width += m.width;
			}
			lineStart = false;
		}
	}
	return line;
}


export function measureRun(ctx: CanvasRenderingContext2D, run:TextRun):MeasuredRun {
	const mr: Partial<MeasuredRun> = {...run};
	ctx.font = run.font;
	let m = measure(ctx, run.text);
	mr.x = m.width;
	mr.ascent = m.fontBoundingBoxAscent;
	mr.descent = m.fontBoundingBoxDescent;
	return mr as MeasuredRun;
}


export function measureRuns(ctx: CanvasRenderingContext2D, runs:TextRun[]):MeasuredRun[] {
	try {
		ctx.save();
		const measured:MeasuredRun[] = [];
		for (let r of runs) {
			measured.push(measureRun(ctx, r));
		}
		return measured;
	} finally {
		ctx.restore();
	}
}


export function measureBlockText(ctx: CanvasRenderingContext2D, runs: TextRun[], width: number, lineHeight: number): MeasuredLine[] {
	const lines: MeasuredLine[] = [];
	let currentLineRuns: MeasuredRun[] = [];
	let currentLineText = "";
	let currentLineWidth = 0;
	let lineStart:boolean = true;

	runs.forEach((run, index) => {
		ctx.font = run.font;
		const whiteSpaceStart = /^\s/.test(run.text.charAt(0));
		const whiteSpaceEnd = run.text.length > 1 && /^\s/.test(run.text.charAt(run.text.length - 1));
		const txt = run.text.trim();
		const words = txt.split(/\s+/);

		for (let w = 0; w < words.length; w++) {
			let word = words[w];
			const hasEndSpace = whiteSpaceEnd && w === (words.length - 1);	// relevant for last word
			const startingSpace = !lineStart && (w == 0 && whiteSpaceStart || w > 0);
			let wordWithSpace = (startingSpace ? " "+word : word);
			let m = measure(ctx, wordWithSpace);
			if ((currentLineWidth + m.width) > width) {
				// This word won't fit in (we must break)
				if (currentLineRuns.length > 0) {
					lines.push({
						text: currentLineText,
						width: currentLineWidth,
						runs: currentLineRuns,
						ascent: maxAscent(currentLineRuns),
						descent: maxDescent(currentLineRuns),
						y: 0
					});
				}
				// We must measure without space
				if (hasEndSpace) word += ' ';
				m = measure(ctx, word);
				currentLineText = word;
				currentLineWidth = m.width;
				currentLineRuns = [];
				addToCurentLine(currentLineRuns, word, 0, m.fontBoundingBoxAscent, m.fontBoundingBoxDescent, run);
			} else {
				// Great it will fit in
				if (hasEndSpace) {
					wordWithSpace += ' ';
					m = measure(ctx, wordWithSpace);
				}
				addToCurentLine(currentLineRuns, wordWithSpace, currentLineWidth, m.fontBoundingBoxAscent, m.fontBoundingBoxDescent, run);
				currentLineText += wordWithSpace;
				currentLineWidth += m.width;
			}
			lineStart = false;
		}
	});

	if (currentLineRuns.length > 0) {
		lines.push({text: currentLineText.trimEnd(), width: currentLineWidth, runs: currentLineRuns, ascent: maxAscent(currentLineRuns), descent: maxDescent(currentLineRuns), y:0 });
	}

	if (lines.length > 0) {
		const firstLine = lines[0];
		for (let i = 0; i < lines.length; i++) {
			const ln = lines[i];
			if (i === 0) ln.y = ln.ascent;
			else ln.y = ln.ascent + i * lineHeight;
		}
	}

	return lines;
}

export function drawTextLine(
	ctx: CanvasRenderingContext2D,
	runs: TextRun[],
	x: number,
	y: number,
	width: number
):number {
	const line = measureTextLine(ctx, runs, width);
	if (line.runs.length === 0) return 0;
	const alpha = ctx.globalAlpha;
	line.runs.forEach(run => {
		ctx.font = run.font;
		if (typeof run.opacity === "number" && run.opacity < 1) {
			// globalAlpha doesn't work in Safari 17.x so I try to modify color if it is in #RGB format
			let alphaColor = addAlpha(run.color, run.opacity);
			if (alphaColor) {
				ctx.fillStyle = alphaColor;
			} else {
				ctx.globalAlpha = alpha * run.opacity;
				ctx.fillStyle = run.color;
			}
		} else ctx.fillStyle = run.color;
		const textToDraw = run.text;
		ctx.fillText(textToDraw, x + run.x, y + line.y);
		ctx.globalAlpha = alpha;
	});
	return line.width;
}

export function drawBlockText(
	ctx: CanvasRenderingContext2D,
	runs: TextRun[],
	x: number,
	y: number,
	width: number,
	height: number,
	lineHeight: number,
	justify: "left" | "center" | "right" = "left",
	align: "top" | "center" | "bottom" = "top"
): number {
	const lines = measureBlockText(ctx, runs, width, lineHeight);
	if (lines.length === 0) return;

	const lastLine = lines[lines.length - 1];
	const blockHeight = lastLine.y + lastLine.descent;

	let startY = y;
	if (align === "center") {
		startY = y + (height - blockHeight) / 2;
	} else if (align === "bottom") {
		startY = y + (height - blockHeight);
	}

	lines.forEach((line, index) => {
		let offsetX = 0;
		if (justify === "center") {
			offsetX = (width - line.width) / 2;
		} else if (justify === "right") {
			offsetX = (width - line.width);
		}

		const alpha = ctx.globalAlpha;
		line.runs.forEach(run => {
			ctx.font = run.font;
			if (typeof run.opacity === "number" && run.opacity < 1) {
				// globalAlpha doesn't work in Safari 17.x so I try to modify color if it is in #RGB format
				let alphaColor = addAlpha(run.color, run.opacity);
				if (alphaColor) {
					ctx.fillStyle = alphaColor;
				} else {
					ctx.globalAlpha = alpha * run.opacity;
					ctx.fillStyle = run.color;
				}
			} else ctx.fillStyle = run.color;
			const textToDraw = run.text;
			ctx.fillText(textToDraw, x + offsetX + run.x, startY + line.y);
			ctx.globalAlpha = alpha;
		});
	});

	return blockHeight;
}

export function addAlpha(color:string, alpha:number):string {
	if (color.length === 7 && color.charAt(0) === "#") {		// Only #RRGGBB format supported
		const aval = Math.round(alpha * 255);
		let hx = aval.toString(16);
		if (hx.length === 1) hx = "0" + hx;
		return color + hx;
	}
	return null;
}